import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    Business as BusinessIcon,
    Close as CloseIcon,
    Description as DescriptionIcon,
    Download as DownloadIcon,
    Event as EventIcon,
    Fingerprint as FingerprintIcon,
    Person as PersonIcon,
    VerifiedUser as TrustCheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { usePolyAuth } from '../../../hooks/PolyAuthContext';
import { formatBiometricConfidence } from '../../trustCheckLogs';
import { formatTimestamp, getLogTypeTitle, getTypeIcon } from '../helpers';
import { NoCertificateTag, PresenceScoreChip, TrustCheckKindBadge } from '../RowIndicators';
import { ParticipantsList } from './ParticipantsList';
import { useMeetingDetails } from './useMeetingDetails';
import { useMeetingReportGen } from './useMeetingReportGen';

export const MeetingDetailsModal = ({ selectedLog, onClose, openURL }) => {
    const { t, i18n } = useTranslation('shared');
    const { api_client } = usePolyAuth();

    const { meetingDetails, loadingDetails, detailsError, reset } = useMeetingDetails(selectedLog);

    const handleCloseDetails = () => {
        reset();
        onClose();
    };

    const { generateReport, generatingReport, reportError } = useMeetingReportGen({
        selectedLog,
        openURL,
        onSuccess: handleCloseDetails,
    });

    if (!selectedLog) return null;

    const formattedTime = formatTimestamp(selectedLog.timestamp, i18n.language);
    const logTypeTitle = getLogTypeTitle(selectedLog.callType, t);
    const isMeeting = selectedLog.callType === 'meeting';
    const isTrustCheck = selectedLog.callType === 'trust_check';

    // Use meeting details if available, otherwise fall back to selectedLog
    const meetingInfo = meetingDetails?.meeting || selectedLog;

    // Inline download handler for the trust-check modal — symmetric with the
    // row-level button but lives inside the dialog so users who open the modal
    // before realizing the affidavit is there can still grab it.
    const handleDownloadAffidavit = async () => {
        if (!selectedLog?.trust_check_session_id || !selectedLog?.app_id) return;
        try {
            const response = await api_client.get(`/v2/affidavits/${selectedLog.app_id}/${selectedLog.trust_check_session_id}/download`);
            const url = response?.data?.url || response?.request?.responseURL;
            if (url) {
                openURL(url);
            } else {
                toast(t('callLogs.failedToDownloadCertificate'), { type: 'error' });
            }
        } catch (err) {
            console.error('Affidavit download failed', err);
            toast(t('callLogs.failedToDownloadCertificate'), { type: 'error' });
        }
    };

    return (
        <Dialog
            open={true}
            onClose={handleCloseDetails}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 2, display: 'flex' }}>
                        {getTypeIcon(selectedLog.callType)}
                    </Box>
                    <Typography variant="h6">
                        {isTrustCheck
                            ? t('callLogs.trustCheckDetails')
                            : t('callLogs.meetingDetails', { type: logTypeTitle })}
                    </Typography>
                </Box>
                <IconButton onClick={handleCloseDetails} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            {isTrustCheck ? (
                                <TrustCheckIcon sx={{ mr: 1 }} color="secondary" />
                            ) : (
                                <EventIcon sx={{ mr: 1 }} color="primary" />
                            )}
                            {isTrustCheck
                                ? (selectedLog.verified_subject_name || t('callLogs.untitled'))
                                : (meetingInfo.title || t('callLogs.untitled'))}
                            {isTrustCheck && (
                                <Box sx={{ ml: 1.5 }}>
                                    <TrustCheckKindBadge linkType={selectedLog.link_type} t={t} />
                                </Box>
                            )}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                        <strong>{t('callLogs.dateLabel')}</strong> {formattedTime.date}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                        <strong>{t('callLogs.timeLabel')}</strong> {formattedTime.time}
                                    </Typography>
                                </Box>
                                {isMeeting && meetingInfo.duration && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Typography variant="body2">
                                            <strong>{t('callLogs.durationLabel')}</strong> {meetingInfo.duration}
                                        </Typography>
                                    </Box>
                                )}
                                {isTrustCheck && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <FingerprintIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Typography variant="body2">
                                            <strong>{t('callLogs.biometricConfidenceLabel')}</strong> {formatBiometricConfidence(selectedLog.biometric_confidence)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box>
                                {isMeeting && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="body2">
                                                <strong>{t('callLogs.hostLabel')}</strong> {meetingInfo.host_name || t('callLogs.unknown')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                            <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="body2">
                                                <strong>{t('callLogs.organizationLabel')}</strong> {meetingInfo.organization || t('callLogs.unknown')}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                                {isTrustCheck && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                            <Typography variant="body2" component="span">
                                                <strong>{t('callLogs.presenceScoreLabel')}</strong>
                                            </Typography>
                                            <PresenceScoreChip score={selectedLog.presence_score} t={t} />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                            <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="body2">
                                                <strong>{t('callLogs.appIdLabel')}</strong> {selectedLog.app_id || t('callLogs.unknown')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                            <DescriptionIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                                <strong>{t('callLogs.trustCheckSessionLabel')}</strong> {selectedLog.trust_check_session_id}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {isMeeting && (
                    <Card variant="outlined">
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                            <Typography variant="h6" sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
                                <PersonIcon sx={{ mr: 1 }} color="primary" />
                                {t('callLogs.participants', { count: meetingDetails?.participants?.total || 0 })}
                            </Typography>
                            <ParticipantsList
                                meetingDetails={meetingDetails}
                                loadingDetails={loadingDetails}
                                detailsError={detailsError}
                            />
                        </CardContent>
                    </Card>
                )}
            </DialogContent>

            {isMeeting && (
                <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
                    {reportError && (
                        <Alert severity="error" sx={{ mr: 2, flex: 1 }}>
                            {reportError}
                        </Alert>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={generatingReport ? <CircularProgress size={20} color="inherit" /> : <DescriptionIcon />}
                        onClick={generateReport}
                        disabled={generatingReport}
                        sx={{ borderRadius: '8px' }}
                    >
                        {generatingReport ? t('callLogs.generating') : t('callLogs.generateReport')}
                    </Button>
                </DialogActions>
            )}
            {isTrustCheck && (
                <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
                    {selectedLog.affidavit_key ? (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadAffidavit}
                            sx={{ borderRadius: '8px' }}
                        >
                            {t('callLogs.downloadCertificate')}
                        </Button>
                    ) : (
                        <NoCertificateTag t={t} />
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};
